// Actual labor cost from timesheet entries (Planning/ActualCosting.md).
//
// Deliberately NOT a second cost engine: each worked entry is turned into a
// call-shaped interval and run through the SAME costForBooking pipeline the
// scheduled/offered figure uses, so rounding, minimums, OT bands, rest gaps
// and holidays can't drift between "offered" and "actual". The only
// addition is unpaidBreakMinutes (see quote.ts WorkSpan), which shortens the
// billed hours without moving the real start/end.
//
// Rounding is by rate type, because a clock session is not a scheduled call:
//   - DAY rate: a person's sessions on one calendar day are POOLED into a single
//     interval first, so the role's rounding (whole hours, or the half-hour rule
//     for standard timekeeping) applies once to the day's total. Clocking out
//     for lunch must not add paid time.
//   - HOURLY: each clock-in/out period rounds to the half hour on its own.
import type { Booking, Call, Position, RateCard, TimesheetEntry } from './types.js';
import { costForBooking, costForBookingByDay, summarizeBudget } from './budget.js';
import { dateKey } from './quote.js';
import { sharedTotal, type SharedCosts } from './sharedDay.js';
import type { OTCostOutput, StandardCostOutput } from './ot-cost.js';
import type { Holiday } from './standard-timekeeping.js';
import type { WorkSpan } from './quote.js';

// Bookings whose entries count toward actuals. A CANCELLED/declined booking's
// stray entries are ignored — that work isn't payable through the booking.
const COSTED_BOOKING_STATES = new Set(['ACCEPTED', 'BOOKED', 'COMPLETED']);

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

type WorkInterval = Call & WorkSpan;

// An entry as a Call-shaped interval keyed by the entry's own id. Entries
// with no clockOut (still OPEN) have no end and can't be costed.
export function entryToWorkInterval(entry: TimesheetEntry): WorkInterval | null {
  if (!entry.clockOut) return null;
  return {
    id: entry.id,
    gigId: entry.gigId,
    orgId: entry.orgId,
    type: 'show',
    startTime: entry.clockIn,
    endTime: entry.clockOut,
    unpaidBreakMinutes: entry.breakMinutes,
    createdAt: entry.clockOut,
    updatedAt: entry.clockOut,
  };
}

export interface ActualCost {
  cost: number;
  // Worked hours after break deduction, summed per entry (before the role's
  // rounding/minimums) — informational; the cost itself is bucketed by day.
  hours: number;
}

// One calendar day's sessions as a single interval spanning first clock-in to
// last clock-out, with everything not worked inside that span (breaks and the
// gaps between sessions) as unpaidBreakMinutes — so the billed duration is
// exactly the pooled worked time, while start/end stay the real first/last
// punches (rest-gap and holiday logic read those).
function poolDay(entries: TimesheetEntry[]): WorkInterval {
  const start = new Date(Math.min(...entries.map(e => e.clockIn.getTime())));
  const end = new Date(Math.max(...entries.map(e => e.clockOut!.getTime())));
  const workedMs = entries.reduce(
    (sum, e) => sum + Math.max(0, e.clockOut!.getTime() - e.clockIn.getTime() - (e.breakMinutes ?? 0) * 60000),
    0,
  );
  return {
    id: entries.map(e => e.id).join('+'),
    gigId: entries[0].gigId,
    orgId: entries[0].orgId,
    type: 'show',
    startTime: start,
    endTime: end,
    unpaidBreakMinutes: (end.getTime() - start.getTime() - workedMs) / 60000,
    createdAt: end,
    updatedAt: end,
  };
}

// The shared setup for costing a booking's entries: intervals built from the
// closed entries (see the rounding note at the top), and the booking
// re-pointed at them by id.
function actualsInputs(
  booking: Booking,
  position: Pick<Position, 'rateType'>,
  entries: TimesheetEntry[],
  timeZone: string,
) {
  const closed = entries.filter(e => e.clockOut);
  const hourly = (booking.rateType ?? position.rateType) === 'hourly';
  let intervals: WorkInterval[];
  if (hourly) {
    intervals = closed
      .map(entryToWorkInterval)
      .filter((i): i is WorkInterval => i !== null)
      .map(i => ({ ...i, roundToHalfHour: true }));
  } else {
    const byDay = new Map<string, TimesheetEntry[]>();
    for (const e of closed) {
      const key = dateKey(e.clockIn, timeZone);
      const list = byDay.get(key);
      if (list) list.push(e);
      else byDay.set(key, [e]);
    }
    intervals = [...byDay.values()].map(poolDay);
  }
  return { intervals, costed: { ...booking, callIds: intervals.map(i => i.id) } };
}

function workedHours(intervals: WorkInterval[]): number {
  const hours = intervals.reduce(
    (sum, i) => sum + Math.max(0, (i.endTime.getTime() - i.startTime.getTime()) / 3600000 - (i.unpaidBreakMinutes ?? 0) / 60),
    0,
  );
  return round2(hours);
}

// Cost of ONE booking from the given entries (already filtered by the caller
// to the statuses it wants). Priced at the booking's own rate, like
// costForBooking — a per-person override beats the position rate.
export function costActualsForBooking(
  booking: Booking,
  position: Pick<Position, 'rate' | 'rateType' | 'rateCardId' | 'rateCardRole'>,
  entries: TimesheetEntry[],
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
): ActualCost {
  const { intervals, costed } = actualsInputs(booking, position, entries, timeZone);
  if (intervals.length === 0) return { cost: 0, hours: 0 };
  return { cost: costForBooking(costed, position, intervals, rateCards, timeZone, holidays, 'actual'), hours: workedHours(intervals) };
}

// Same costing, but keyed by calendar day (org time zone) with each day's
// hour/overtime breakdown — what a payroll report stores per person.
export function costActualsByDay(
  booking: Booking,
  position: Pick<Position, 'rate' | 'rateType' | 'rateCardId' | 'rateCardRole'>,
  entries: TimesheetEntry[],
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
): { byDay: Map<string, OTCostOutput | StandardCostOutput>; hours: number } {
  const { intervals, costed } = actualsInputs(booking, position, entries, timeZone);
  if (intervals.length === 0) return { byDay: new Map(), hours: 0 };
  return { byDay: costForBookingByDay(costed, position, intervals, rateCards, timeZone, holidays, 'actual'), hours: workedHours(intervals) };
}

export interface ActualsPositionSummary {
  positionId: string;
  role: string;
  label?: string;
  budgeted: number;
  // Existing offered/scheduled cost (summarizeBudget.actualOfferedCost).
  scheduledCost: number;
  // APPROVED entries only — the official actual.
  approvedCost: number;
  approvedHours: number;
  // APPROVED + PENDING: what it becomes if everything awaiting review is approved.
  projectedCost: number;
  pendingHours: number;
  // Entries still OPEN (clocked in, no clock-out) and PENDING review.
  openCount: number;
  pendingCount: number;
  // approvedCost - budgeted (positive = over). 0 when no budget is set.
  varianceVsBudget: number;
  overBudget: boolean;
  projectedOverBudget: boolean;
}

export interface BookingActuals {
  approvedCost: number;
  approvedHours: number;
  projectedCost: number;
  pendingHours: number;
}

export interface ActualsSummary {
  positions: ActualsPositionSummary[];
  totals: {
    budgeted: number;
    scheduledCost: number;
    approvedCost: number;
    approvedHours: number;
    projectedCost: number;
    pendingHours: number;
    openCount: number;
    pendingCount: number;
    overBudget: boolean;
    projectedOverBudget: boolean;
  };
  // Per booking, for the per-person rollup.
  byBooking: Map<string, BookingActuals>;
}

export function summarizeActuals(
  positions: Position[],
  bookingsByPosition: Record<string, Booking[]>,
  entries: TimesheetEntry[],
  calls: Call[],
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
  // Shared-day allocations (Planning/SharedDay.md) for bookings that are part
  // of one: their cost is their share of the pooled person-day.
  shared?: SharedCosts,
): ActualsSummary {
  const scheduled = new Map(
    summarizeBudget(positions, bookingsByPosition, calls, rateCards, timeZone, holidays, shared?.estimate).map(s => [s.positionId, s]),
  );
  const entriesByBooking = new Map<string, TimesheetEntry[]>();
  for (const e of entries) {
    const list = entriesByBooking.get(e.bookingId);
    if (list) list.push(e);
    else entriesByBooking.set(e.bookingId, [e]);
  }

  const byBooking: ActualsSummary['byBooking'] = new Map();
  const rows = positions.map((pos): ActualsPositionSummary => {
    const budgeted = pos.maxBudget ?? 0;
    let approvedCost = 0;
    let approvedHours = 0;
    let projectedCost = 0;
    let pendingHours = 0;
    let openCount = 0;
    let pendingCount = 0;

    for (const booking of bookingsByPosition[pos.id] ?? []) {
      if (!COSTED_BOOKING_STATES.has(booking.state)) continue;
      const mine = entriesByBooking.get(booking.id) ?? [];
      const approved = mine.filter(e => e.status === 'APPROVED');
      const pending = mine.filter(e => e.status === 'PENDING');
      openCount += mine.filter(e => e.status === 'OPEN').length;
      pendingCount += pending.length;

      const a = costActualsForBooking(booking, pos, approved, rateCards, timeZone, holidays);
      const p = costActualsForBooking(booking, pos, [...approved, ...pending], rateCards, timeZone, holidays);
      // A booking in a shared day costs its allocated share of the pooled day,
      // not what its own entries would cost alone (hours stay its own).
      if (shared?.approved?.has(booking.id)) a.cost = sharedTotal(shared.approved, booking.id);
      if (shared?.projected?.has(booking.id)) p.cost = sharedTotal(shared.projected, booking.id);
      approvedCost += a.cost;
      approvedHours += a.hours;
      projectedCost += p.cost;
      pendingHours += round2(p.hours - a.hours);
      byBooking.set(booking.id, {
        approvedCost: a.cost,
        approvedHours: a.hours,
        projectedCost: p.cost,
        pendingHours: round2(p.hours - a.hours),
      });
    }

    approvedCost = round2(approvedCost);
    projectedCost = round2(projectedCost);
    return {
      positionId: pos.id,
      role: pos.role,
      label: pos.label,
      budgeted,
      scheduledCost: scheduled.get(pos.id)?.actualOfferedCost ?? 0,
      approvedCost,
      approvedHours: round2(approvedHours),
      projectedCost,
      pendingHours: round2(pendingHours),
      openCount,
      pendingCount,
      varianceVsBudget: budgeted > 0 ? round2(approvedCost - budgeted) : 0,
      overBudget: budgeted > 0 && approvedCost > budgeted,
      projectedOverBudget: budgeted > 0 && projectedCost > budgeted,
    };
  });

  const sum = (f: (r: ActualsPositionSummary) => number) => round2(rows.reduce((t, r) => t + f(r), 0));
  const budgeted = sum(r => r.budgeted);
  const approvedCost = sum(r => r.approvedCost);
  const projectedCost = sum(r => r.projectedCost);
  return {
    positions: rows,
    totals: {
      budgeted,
      scheduledCost: sum(r => r.scheduledCost),
      approvedCost,
      approvedHours: sum(r => r.approvedHours),
      projectedCost,
      pendingHours: sum(r => r.pendingHours),
      openCount: rows.reduce((t, r) => t + r.openCount, 0),
      pendingCount: rows.reduce((t, r) => t + r.pendingCount, 0),
      overBudget: budgeted > 0 && approvedCost > budgeted,
      projectedOverBudget: budgeted > 0 && projectedCost > budgeted,
    },
    byBooking,
  };
}
